import React, { useState, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import Toast from "./Toast";

/**
 * ToastContainer - Manages multiple toasts with a global API
 */
/**
 * ToastContainer - Manages multiple toasts with a global API
 */
const ToastContainer = ({ toasts, setToasts }) => {
  // Remove a toast by id
  const removeToast = useCallback(
    (id) => {
      setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
    },
    [setToasts]
  );

  return ReactDOM.createPortal(
    <>
      {/* Top Right */}
      <div className="fixed top-4 right-4 z-50 flex flex-col gap-2">
        {toasts
          .filter((toast) => toast.position === "top-right")
          .map((toast) => (
            <Toast
              key={toast.id}
              message={toast.message}
              type={toast.type}
              onClose={() => removeToast(toast.id)}
              duration={toast.duration}
              position="top-right"
            />
          ))}
      </div>

      {/* Top Left */}
      <div className="fixed top-4 left-4 z-50 flex flex-col gap-2">
        {toasts
          .filter((toast) => toast.position === "top-left")
          .map((toast) => (
            <Toast
              key={toast.id}
              message={toast.message}
              type={toast.type}
              onClose={() => removeToast(toast.id)}
              duration={toast.duration}
              position="top-left"
            />
          ))}
      </div>

      {/* Bottom Right */}
      <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2">
        {toasts
          .filter((toast) => toast.position === "bottom-right")
          .map((toast) => (
            <Toast
              key={toast.id}
              message={toast.message}
              type={toast.type}
              onClose={() => removeToast(toast.id)}
              duration={toast.duration}
              position="bottom-right"
            />
          ))}
      </div>

      {/* Bottom Left */}
      <div className="fixed bottom-4 left-4 z-50 flex flex-col gap-2">
        {toasts
          .filter((toast) => toast.position === "bottom-left")
          .map((toast) => (
            <Toast
              key={toast.id}
              message={toast.message}
              type={toast.type}
              onClose={() => removeToast(toast.id)}
              duration={toast.duration}
              position="bottom-left"
            />
          ))}
      </div>

      {/* Top Center */}
      <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 flex flex-col gap-2">
        {toasts
          .filter((toast) => toast.position === "top-center")
          .map((toast) => (
            <Toast
              key={toast.id}
              message={toast.message}
              type={toast.type}
              onClose={() => removeToast(toast.id)}
              duration={toast.duration}
              position="top-center"
            />
          ))}
      </div>

      {/* Bottom Center */}
      <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50 flex flex-col gap-2">
        {toasts
          .filter((toast) => toast.position === "bottom-center")
          .map((toast) => (
            <Toast
              key={toast.id}
              message={toast.message}
              type={toast.type}
              onClose={() => removeToast(toast.id)}
              duration={toast.duration}
              position="bottom-center"
            />
          ))}
      </div>
    </>,
    document.body
  );
};
// Toast service for adding toasts from anywhere in the application
let toastContainerId = 0;
const toastContainerMap = new Map();

// Create a global toast service
export const toastService = {
  // Show a toast notification
  show: (message, options = {}) => {
    const id = ++toastContainerId;
    const toast = {
      id,
      message,
      type: options.type || "info",
      duration: options.duration !== undefined ? options.duration : 5000,
      position: options.position || "top-right",
    };

    // Add toast to the state
    const setToasts = toastContainerMap.get("setToasts");
    if (setToasts) {
      setToasts((prev) => [...prev, toast]);
    }

    // Return the id for potential manual closing
    return id;
  },

  // Success toast shorthand
  success: (message, options = {}) => {
    return toastService.show(message, { ...options, type: "success" });
  },

  // Warning toast shorthand
  warning: (message, options = {}) => {
    return toastService.show(message, { ...options, type: "warning" });
  },

  // Error toast shorthand
  error: (message, options = {}) => {
    return toastService.show(message, { ...options, type: "error" });
  },

  // Info toast shorthand
  info: (message, options = {}) => {
    return toastService.show(message, { ...options, type: "info" });
  },

  // Close a specific toast by id
  close: (id) => {
    const setToasts = toastContainerMap.get("setToasts");
    if (setToasts) {
      setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }
  },

  // Close all toasts
  closeAll: () => {
    const setToasts = toastContainerMap.get("setToasts");
    if (setToasts) {
      setToasts([]);
    }
  },
};

// ToastProvider - Wrap your app with this component
export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  // Register the setState function with the global map
  useEffect(() => {
    toastContainerMap.set("setToasts", setToasts);
    return () => {
      toastContainerMap.delete("setToasts");
    };
  }, []);

  return (
    <>
      {children}
      <ToastContainer toasts={toasts} setToasts={setToasts} />
    </>
  );
};

export default ToastContainer;
